import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Image } from 'react-bootstrap';
import { companyService } from '../../services/api';
import { Company } from '../../types';
import { toast } from 'react-toastify';

const Settings: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [stampPreview, setStampPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    ice: '',
    if_number: '',
    cnss: '',
    rc: '',
    address: '',
    email: '',
    phone: '',
    fax: '',
    header_template: '',
    footer_template: '',
    logo: null as File | null,
    stamp_path: null as File | null,
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);

      const response = await companyService.getSettings();
      const companyData = response.data.company;

      setCompany(companyData);

      setFormData({
        name: companyData.name || '',
        ice: companyData.ice || '',
        if_number: companyData.if_number || '',
        cnss: companyData.cnss || '',
        rc: companyData.rc || '',
        address: companyData.address || '',
        email: companyData.email || '',
        phone: companyData.phone || '',
        fax: companyData.fax || '',
        header_template: companyData.header_template || '',
        footer_template: companyData.footer_template || '',
        logo: null,
        stamp_path: null,
      });

      setLogoPreview(response.data.logo_url || null);
      setStampPreview(response.data.stamp_url || null);

    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;

    if (!files || files.length === 0) return;

    const file = files[0];

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté (PNG, JPG uniquement)');
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: file,
    }));

    const previewUrl = URL.createObjectURL(file);

    if (name === 'logo') setLogoPreview(previewUrl);
    if (name === 'stamp_path') setStampPreview(previewUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (value instanceof File) {
            data.append(key, value);
          } else {
            data.append(key, String(value));
          }
        }
      });

      await companyService.updateSettings(data);

      toast.success('Paramètres sauvegardés avec succès');
      fetchCompanySettings();

    } catch (error: any) {
      console.error(error);

      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }

    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center">Chargement...</div>;
  }

  return (
    <div>
      <h1 className="mb-4">Paramètres de l'entreprise</h1>

      <Form onSubmit={handleSubmit}>

        {/* INFOS */}
        <Card className="mb-4">
          <Card.Header><h5>Informations générales</h5></Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Raison sociale *</Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* LOGO + CACHET */}
        <Card className="mb-4">
          <Card.Header><h5>Logo & Cachet</h5></Card.Header>
          <Card.Body>
            <Row>

              {/* LOGO */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo</Form.Label>
                  <Form.Control
                    type="file"
                    name="logo"
                    accept="image/png,image/jpeg"
                    onChange={handleFileChange}
                  />
                </Form.Group>

                {logoPreview && (
                  <Image
                    src={logoPreview}
                    thumbnail
                    style={{ maxHeight: 120, objectFit: 'contain' }}
                  />
                )}
              </Col>

              {/* CACHET */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cachet</Form.Label>
                  <Form.Control
                    type="file"
                    name="stamp_path"
                    accept="image/png,image/jpeg"
                    onChange={handleFileChange}
                  />
                </Form.Group>

                {stampPreview && (
                  <Image
                    src={stampPreview}
                    thumbnail
                    style={{ maxHeight: 120, objectFit: 'contain' }}
                  />
                )}
              </Col>

            </Row>
          </Card.Body>
        </Card>

        {/* FOOTER / HEADER */}
        <Card className="mb-4">
          <Card.Header><h5>Personnalisation</h5></Card.Header>
          <Card.Body>

            <Form.Group className="mb-3">
              <Form.Label>En-tête</Form.Label>
              <Form.Control
                as="textarea"
                name="header_template"
                value={formData.header_template}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pied de page</Form.Label>
              <Form.Control
                as="textarea"
                name="footer_template"
                value={formData.footer_template}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Alert variant="info">
              Ces textes apparaîtront sur vos PDF
            </Alert>

          </Card.Body>
        </Card>

        <div className="text-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

      </Form>
    </div>
  );
};

export default Settings;